import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  GridItem,
  Spinner,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { DateRangePicker } from 'react-date-range';
import { enGB } from 'date-fns/locale';
import * as dateFns from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { FaChevronDown } from 'react-icons/fa';
import { FiInfo } from 'react-icons/fi';
import { GetCountryRevenue } from 'reactRedux/thunks/dashboardThunk/dashboardThunk';
import { formatCurrency } from 'utils/CommonFunction';
import { customStaticRanges, sideBarRoutes } from 'utils/StaticData';

// Small "i" tooltip used next to the reconciliation labels.
const InfoTip = ({ label }) => (
  <Tooltip
    label={label}
    hasArrow
    placement="top"
    fontSize="xs"
    px="10px"
    py="8px"
    maxW="260px"
    borderRadius="8px"
    bg="gray.700"
    color="white"
  >
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      ml="5px"
      color="gray.400"
      cursor="help"
    >
      <FiInfo size={13} />
    </Box>
  </Tooltip>
);

InfoTip.propTypes = {
  label: PropTypes.string.isRequired,
};

// One "label: value" line for the cost breakdown (captured / refund / fees …).
const BreakdownRow = ({ label, value, currency, negative }) => (
  <Flex justify="space-between">
    <Text fontSize="sm" color="gray.500">
      {label}
    </Text>
    <Text
      fontSize="sm"
      fontWeight="500"
      color={negative ? 'red.500' : 'gray.700'}
    >
      {negative ? '-' : ''}
      {formatCurrency(value, currency)}
    </Text>
  </Flex>
);

BreakdownRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  negative: PropTypes.bool,
};

// Region name → flag emoji. Add new regions here; unknown regions show a globe.
const REGION_FLAG = {
  Netherlands: '🇳🇱',
  Spain: '🇪🇸',
  Luxembourg: '🇱🇺',
  'United Kingdom': '🇬🇧',
  France: '🇫🇷',
  Germany: '🇩🇪',
  Belgium: '🇧🇪',
};

// Country flag emoji for the card header.
const Flag = ({ regionName }) => (
  <Text
    as="span"
    fontSize="lg"
    lineHeight="1"
    role="img"
    aria-label={regionName}
  >
    {REGION_FLAG[regionName] || '🌐'}
  </Text>
);

Flag.propTypes = {
  regionName: PropTypes.string.isRequired,
};

const RegionCard = ({ region }) => {
  const hasStripe =
    region.stripeNetVolume !== null && region.stripeNetVolume !== undefined;
  const hasDifference =
    region.difference !== null && region.difference !== undefined;
  const isMatch = hasDifference && Math.abs(region.difference) < 0.01;

  // Difference as a % of our Net Revenue — computed by the backend.
  const diffPercent =
    region.differencePercentage !== null &&
    region.differencePercentage !== undefined
      ? region.differencePercentage
      : null;

  return (
    <Box
      borderRadius="12px"
      border="1px solid"
      borderColor="#D9D9D9"
      bg="white"
      overflow="hidden"
    >
      {/* Header with country flag */}
      <Flex
        justify="space-between"
        align="center"
        bg="#eef1fb"
        px="16px"
        py="12px"
      >
        <Flex align="center" gap="8px" minW="0">
          <Flag regionName={region.regionName} />
          <Text fontWeight="700" fontSize="md" color="gray.700" noOfLines={1}>
            {region.regionName}
          </Text>
        </Flex>
        <Text fontSize="sm" fontWeight="600" color="gray.500">
          {region.currency}
        </Text>
      </Flex>

      <Flex direction="column" gap="6px" px="16px" py="14px">
        <BreakdownRow
          label="Captured"
          value={region.capturedAmount}
          currency={region.currency}
        />
        <BreakdownRow
          label="Refund"
          value={region.refundAmount}
          currency={region.currency}
          negative
        />
        <BreakdownRow
          label="Stripe Fees"
          value={region.stripeFees}
          currency={region.currency}
          negative
        />
        <BreakdownRow
          label="Dispute"
          value={region.disputeAmount}
          currency={region.currency}
          negative
        />
        <BreakdownRow
          label="Dispute Fees"
          value={region.disputeFees}
          currency={region.currency}
          negative
        />

        <Divider borderColor="gray.300" my="6px" />

        <Box>
          {/* Net Revenue */}
          <Flex justify="space-between" align="center" gap="8px">
            <Flex align="center">
              <Text fontSize="sm" fontWeight="700" color="gray.700">
                Net Volume
              </Text>
              <InfoTip label="Our earnings = captured payments - refunds - Stripe fees - disputes" />
            </Flex>
            <Flex align="baseline" gap="6px" whiteSpace="nowrap">
              <Text fontSize="md" fontWeight="700" color="blue.600">
                {formatCurrency(region.netRevenue, region.currency)}
              </Text>
              {region.percentageChange !== null &&
              region.percentageChange !== undefined ? (
                <Text
                  fontSize="xs"
                  fontWeight="600"
                  color={region.percentageChange >= 0 ? 'green.500' : 'red.500'}
                >
                  ({region.percentageChange >= 0 ? '↑' : '↓'}{' '}
                  {Math.abs(region.percentageChange).toFixed(1)}%)
                </Text>
              ) : (
                <Text fontSize="xs" color="gray.400">
                  No prev data
                </Text>
              )}
            </Flex>
          </Flex>

          {/* Stripe Net Volume (via API / nightly snapshot) */}
          <Flex justify="space-between" align="center" mt="10px" gap="8px">
            <Flex align="center" minW="0">
              <Text fontSize="sm" color="gray.500" noOfLines={1}>
                Net Volume (Stripe)
              </Text>
              <InfoTip label="Stripe's own net figure. Today's transactions may not be included yet." />
            </Flex>
            <Text
              fontSize="sm"
              fontWeight="600"
              color="gray.700"
              whiteSpace="nowrap"
            >
              {hasStripe
                ? formatCurrency(region.stripeNetVolume, region.currency)
                : 'N/A'}
            </Text>
          </Flex>

          {/* Difference */}
          <Flex justify="space-between" align="center" mt="6px" gap="8px">
            <Flex align="center">
              <Text fontSize="sm" color="gray.500">
                Difference
              </Text>
              <InfoTip label="Gap Between System Net Volume and Stripe Net Volume" />
            </Flex>
            {hasDifference ? (
              <Flex align="baseline" gap="5px" whiteSpace="nowrap">
                <Text
                  fontSize="sm"
                  fontWeight="700"
                  color={isMatch ? 'green.500' : 'orange.500'}
                >
                  {region.difference > 0 ? '+' : ''}
                  {formatCurrency(region.difference, region.currency)}
                </Text>
                {diffPercent !== null && (
                  <Text
                    fontSize="xs"
                    fontWeight="600"
                    color={isMatch ? 'green.500' : 'orange.500'}
                  >
                    ({diffPercent >= 0 ? '+' : ''}
                    {diffPercent.toFixed(2)}%)
                  </Text>
                )}
              </Flex>
            ) : (
              <Text fontSize="sm" fontWeight="500" color="gray.400">
                N/A
              </Text>
            )}
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
};

RegionCard.propTypes = {
  region: PropTypes.shape({
    regionId: PropTypes.number.isRequired,
    regionName: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    capturedAmount: PropTypes.number.isRequired,
    refundAmount: PropTypes.number.isRequired,
    stripeFees: PropTypes.number.isRequired,
    disputeAmount: PropTypes.number.isRequired,
    disputeFees: PropTypes.number.isRequired,
    netRevenue: PropTypes.number.isRequired,
    stripeNetVolume: PropTypes.number,
    difference: PropTypes.number,
    differencePercentage: PropTypes.number,
    percentageChange: PropTypes.number,
  }).isRequired,
};

export default function CountryRevenueOverview() {
  const dispatch = useDispatch();
  const { countryRevenueData, isCountryRevenueLoading } = useSelector(
    (state) => state.DashboardSlice,
  );
  const { adminDetails } = useSelector((state) => state.authSlice);
  // Dealers only ever see their own country, so "Country-wise" reads wrong.
  const isDealer = adminDetails?.role === sideBarRoutes?.Dealer;

  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarFilterTitle, setCalendarFilterTitle] =
    useState('Last 30 days');
  const [tempCalendarFilterTitle, setTempCalendarFilterTitle] =
    useState('Last 30 days');
  const [dateRange] = useState([
    {
      startDate: dateFns.addDays(new Date(), -29),
      endDate: new Date(),
      key: 'selection',
    },
  ]);
  const [tempDateRange, setTempDateRange] = useState(dateRange);
  const [appliedDateRange, setAppliedDateRange] = useState(dateRange);

  const fetchData = useCallback(
    (range) => {
      const start = dateFns.format(range[0].startDate, 'yyyy/MM/dd');
      const end = dateFns.format(range[0].endDate, 'yyyy/MM/dd');
      dispatch(GetCountryRevenue({ start, end }));
    },
    [dispatch],
  );

  useEffect(() => {
    fetchData(dateRange);
  }, [fetchData, dateRange]);

  const handleSelect = useCallback((ranges) => {
    const matched = customStaticRanges.find(
      (r) =>
        r.range().startDate.getTime() ===
          ranges.selection.startDate.getTime() &&
        r.range().endDate.getTime() === ranges.selection.endDate.getTime(),
    );
    setTempCalendarFilterTitle(matched?.label ?? 'Custom');
    setTempDateRange([ranges.selection]);
  }, []);

  const handleApply = useCallback(() => {
    setCalendarFilterTitle(tempCalendarFilterTitle);
    setAppliedDateRange(tempDateRange);
    setShowCalendar(false);
    fetchData(tempDateRange);
  }, [tempDateRange, tempCalendarFilterTitle, fetchData]);

  const handleClear = useCallback(() => {
    setTempDateRange(dateRange);
    setTempCalendarFilterTitle(calendarFilterTitle);
    setShowCalendar(false);
  }, [dateRange, calendarFilterTitle]);

  const isEmpty = !isCountryRevenueLoading && countryRevenueData.length === 0;

  return (
    <Box>
      <Flex
        justify="space-between"
        align="center"
        mb="20px"
        flexWrap="wrap"
        gap="12px"
      >
        <Box>
          <Text fontSize="lg" fontWeight="700" color="gray.700">
            {isDealer ? 'Net Revenue' : 'Country-wise Net Revenue'}
          </Text>
          <Text fontSize="sm" fontWeight="600" color="gray.600">
            {dateFns.format(appliedDateRange[0].startDate, 'dd-MM-yyyy')} -{' '}
            {dateFns.format(appliedDateRange[0].endDate, 'dd-MM-yyyy')}
          </Text>
        </Box>

        <Box position="relative">
          <Button
            py="10px"
            px="15"
            fontWeight="semibold"
            type="button"
            bg="white"
            border="1px solid"
            borderColor="reviveGray"
            color="#292D32"
            borderRadius="8px"
            onClick={() => setShowCalendar(!showCalendar)}
            justifyContent="space-between"
            isDisabled={isCountryRevenueLoading}
          >
            {calendarFilterTitle}{' '}
            <FaChevronDown size={11} style={{ marginLeft: '8px' }} />
          </Button>

          {showCalendar && (
            <>
              <Box
                position="fixed"
                bg="rgb(0 0 0 / 40%)"
                top="0"
                left="0"
                right="0"
                bottom="0"
                zIndex="9"
                onClick={() => setShowCalendar(false)}
              />
              <Box
                p="32px"
                mt="8px"
                position="absolute"
                right="0"
                zIndex="10"
                bg="white"
                boxShadow="0px 0px 40px rgba(32, 57, 70, 0.06)"
                borderRadius="20px"
                className="custom-date-picker"
              >
                <DateRangePicker
                  ranges={tempDateRange}
                  onChange={handleSelect}
                  showSelectionPreview={true}
                  moveRangeOnFirstSelection={false}
                  months={2}
                  direction="horizontal"
                  locale={enGB}
                  minDate={new Date(2000, 0, 1)}
                  maxDate={new Date()}
                  staticRanges={customStaticRanges}
                  inputRanges={[]}
                />
                <Flex justifyContent="right" gap="12px" marginTop="30px">
                  <Button
                    className="clear-btn"
                    border="1px solid"
                    borderColor="reviveGray"
                    borderRadius="10px"
                    onClick={handleClear}
                  >
                    Clear
                  </Button>
                  <Button
                    className="apply-btn"
                    bg="#6658fd"
                    border="1px solid"
                    borderColor="#6658fd"
                    color="white"
                    borderRadius="10px"
                    _hover={{ bg: 'transparent', color: '#6658fd' }}
                    onClick={handleApply}
                  >
                    Apply
                  </Button>
                </Flex>
              </Box>
            </>
          )}
        </Box>
      </Flex>

      {isCountryRevenueLoading && (
        <Flex justify="center" align="center" minH="200px">
          <Spinner size="lg" color="blue.500" />
        </Flex>
      )}

      {isEmpty && (
        <Flex justify="center" align="center" minH="200px">
          <Text color="gray.400" fontSize="sm">
            No revenue data for selected period.
          </Text>
        </Flex>
      )}

      {/* Same grid for every role. Admins see every country; dealers are
          backend-scoped to their own region, so they get a single tile. */}
      {!isCountryRevenueLoading && countryRevenueData.length > 0 && (
        <Grid
          templateColumns={{
            base: 'repeat(1, 1fr)',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
            xl: 'repeat(4, 1fr)',
          }}
          gap="16px"
          mb="28px"
        >
          {countryRevenueData.map((region) => (
            <GridItem key={region.regionId}>
              <RegionCard region={region} />
            </GridItem>
          ))}
        </Grid>
      )}
    </Box>
  );
}
